/**
 * Entity Source Resolver — single source of truth for "where does the data
 * for entity slug X actually live?".
 *
 * Replaces the hardcoded `ENTITY_BINDINGS` constant with a registry that
 * covers all known slugs (15 entity_types + 6 marketplace kinds) and tells
 * the consumer whether each one is `ready` (real table) or `coming-soon`
 * (Origami bridge / notes table not yet built).
 *
 * Three origins:
 *   - `marketplace` : standalone gam.co.il tables (talents, jobs, projects, ...)
 *   - `origami`     : data lives in Origami CRM, accessed via fb_field_values
 *                     (Phase 1c — bridge in development)
 *   - `tiptap-notes`: data is a TipTap document. Requires a `notes` table
 *                     (Phase 6 — TipTap stabilization will create this)
 *
 * When status is `coming-soon`, the dashboard renders a friendly placeholder
 * instead of 404'ing — the URL is valid, the data backend just isn't wired.
 *
 * Refs:
 *   - docs/specs/internal-tools-unification.md (Phase 1a)
 *   - AD-2026-006 Data Source Hierarchy
 *   - AD-2026-001 Semantic Memory (entity_types is canonical schema)
 */

export type EntitySourceStatus = "ready" | "coming-soon";

/** Where the row data lives. Determines the fetch strategy.
 *  `reference` = a shared vocabulary "database" (reference_lists / reference_items).
 *  Per Gal's decision (2026-06-30): a reference list is just another database the
 *  resolver knows — lens-on-record by `list_slug`, same engine, lightweight table. */
export type EntitySourceOrigin =
  | "marketplace"
  | "origami"
  | "tiptap-notes"
  | "reference"
  /** Read-only Fireberry mirror lens over fb_records (object_type_id filter).
   *  Added for the CRM Platform (13.07.2026) — the entity library is the
   *  platform's single import surface; canonical writes never target the
   *  mirror. */
  | "fireberry-mirror";

/** Maps a column or jsonb path on the source row to a FilterContext key. */
export interface EntityFieldMapping {
  /** Top-level column on the entity row (e.g., 'phone' on talents table). */
  column?: string;
  /** Path inside a jsonb column (default: 'meta'). E.g., 'phone' reads
   *  from `row.meta.phone`. Used for lens-on-record entities (vb_records). */
  metaKey?: string;
  /** Context key consumed by widgets (e.g., "phone", "email", "id"). */
  contextKey: string;
}

export interface EntitySource {
  /** URL-safe identifier — matches `entity_kinds_registry.kind`
   *  or `entity_types.slug`. */
  slug: string;
  /** Hebrew display label (used in headers, breadcrumbs, badges). */
  labelHe: string;
  /** Whether the data layer is wired. */
  status: EntitySourceStatus;
  /** Origin of the row data. */
  origin: EntitySourceOrigin;

  // ── Only present when status === "ready" ────────────────────────
  /** Supabase table name (or future view). */
  table?: string;
  /** Primary key column on the source table. */
  idColumn?: string;
  /** Column rendered as the entity's display name. */
  displayField?: string;
  /** Optional secondary field shown beneath the display name. */
  subtitleField?: string;
  /** Optional filter — adds `.eq('entity_type', filter)` to the query.
   *  Used when multiple slugs share one table (vb_records lens pattern). */
  entityTypeFilter?: string;
  /** Name of the jsonb column that holds extra fields (default: 'meta').
   *  Only relevant when fieldMappings include `metaKey` entries. */
  metaColumn?: string;
  /** Maps source columns/paths → context keys (used by widgets). */
  fieldMappings?: EntityFieldMapping[];
  /** Column the `entityTypeFilter` is applied to. Defaults to 'entity_type'
   *  (the vb_records lens pattern). Reference lists set 'list_slug'. */
  filterColumn?: string;
  /** jsonb column + key to read the display label from when there is no flat
   *  display column (e.g. reference_items.label->>'he'). */
  displayJsonbKey?: { column: string; key: string };
  /** Soft-delete column on a marketplace table. When declared, every read
   *  path (list, single row, context) adds `.is(deletedColumn, null)`.
   *  Declared explicitly per source and never inferred — a filter on a
   *  column that does not exist fails the whole query. vb_records lenses
   *  keep their own `is_deleted = false` filter and do not use this. */
  deletedColumn?: string;

  // ── Only present when status === "coming-soon" ──────────────────
  /** Hebrew explanation shown to the user. */
  comingSoonReason?: string;
}

/** The reference vocabularies seeded in public.reference_items
 *  (Milestone 1 — vBudget v2 unified financial layer). Each is a shared
 *  "database" consumed by select fields via options={source:'reference'}. */
const REFERENCE_LISTS: ReadonlyArray<{ slug: string; labelHe: string; icon: string }> = [
  { slug: "expense_category", labelHe: "קטגוריות הוצאה", icon: "🏷️" },
  { slug: "payment_instrument", labelHe: "אמצעי תשלום", icon: "💳" },
  { slug: "ownership_type", labelHe: "סוג בעלות", icon: "👥" },
  { slug: "currency", labelHe: "מטבע", icon: "💱" },
];

/** Build an EntitySource for a reference list (lens-on-record by list_slug
 *  over the shared reference_items table). */
function referenceSource(slug: string, labelHe: string): EntitySource {
  return {
    slug,
    labelHe,
    status: "ready",
    origin: "reference",
    table: "reference_items",
    idColumn: "item_key",
    displayField: "label",
    displayJsonbKey: { column: "label", key: "he" },
    filterColumn: "list_slug",
    entityTypeFilter: slug,
    fieldMappings: [
      { column: "item_key", contextKey: "entity_id" },
      { column: "parent_key", contextKey: "parent" },
      { column: "sort_order", contextKey: "sort" },
    ],
  };
}

/** Default meta-key mappings used by every vb_records-backed lens.
 *  Extracts canonical fields (phone, email, full_name, status) from
 *  `meta` jsonb so widgets get the same FilterContext shape regardless
 *  of which lens is open. */
const VB_RECORDS_BASE_MAPPINGS: EntityFieldMapping[] = [
  { column: "id", contextKey: "entity_id" },
  { metaKey: "phone", contextKey: "phone" },
  { metaKey: "email", contextKey: "email" },
  { metaKey: "full_name", contextKey: "name" },
  { metaKey: "status", contextKey: "status" },
];

/**
 * The registry. Keys are slugs; values are EntitySource definitions.
 *
 * Hardcoded for Phase 1a. In Phase 1c (Origami bridge) and Phase 6 (notes
 * table), entries will be promoted from `coming-soon` to `ready` and given
 * proper `table`/`fieldMappings`. The labels could in theory be read from
 * `entity_types` + `entity_kinds_registry` at runtime, but hardcoding keeps
 * the resolver synchronous and avoids a DB roundtrip on every page render.
 */
const SOURCES: Record<string, EntitySource> = {
  // ╭─ Marketplace kinds — gam.co.il standalone tables ─╮
  talent: {
    slug: "talent",
    labelHe: "טאלנט",
    status: "ready",
    origin: "marketplace",
    table: "talents",
    idColumn: "talent_id",
    deletedColumn: "deleted_at",
    displayField: "name",
    subtitleField: "primary_role",
    fieldMappings: [
      { column: "talent_id", contextKey: "entity_id" },
      { column: "phone", contextKey: "phone" },
      { column: "email", contextKey: "email" },
      { column: "id_number", contextKey: "id" },
      { column: "city", contextKey: "city" },
      { column: "primary_role", contextKey: "role" },
    ],
  },

  job: {
    slug: "job",
    labelHe: "משרה",
    status: "ready",
    origin: "marketplace",
    table: "jobs",
    idColumn: "job_id",
    deletedColumn: "deleted_at",
    displayField: "title",
    fieldMappings: [
      { column: "job_id", contextKey: "entity_id" },
    ],
  },

  project: {
    slug: "project",
    labelHe: "פרויקט",
    status: "ready",
    origin: "marketplace",
    // Schema corrected 2026-04-29 (FIX-1): `projects` table is empty;
    // populated, Notion-synced PM data lives in `pm_projects` (35 rows,
    // re-measured 18.09.2026 — the note said 17).
    // Mirrors the source-of-truth in entity-bindings.ts.
    table: "pm_projects",
    idColumn: "id",
    deletedColumn: "deleted_at",
    displayField: "title",
    subtitleField: "status",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "notion_id", contextKey: "notion_id" },
      { column: "status", contextKey: "status" },
      { column: "phase", contextKey: "phase" },
      { column: "app_notion_id", contextKey: "app_id" },
    ],
  },

  icount_client: {
    slug: "icount_client",
    labelHe: "לקוח iCount",
    status: "ready",
    origin: "marketplace",
    // 💳 iCount Integration App, Sprint 1 (2026-07-13). Own dedicated table
    // (icount_clients), not a vb_records lens — mirrors iCount's client/get_list,
    // read-only. subject_key = normalized vat_id, same 8->9 pad as
    // v_investigator_crm_subjects (318/325 already match investigator subjects).
    table: "icount_clients",
    idColumn: "client_id",
    displayField: "client_name",
    subtitleField: "company_name",
    fieldMappings: [
      { column: "client_id", contextKey: "entity_id" },
      { column: "email", contextKey: "email" },
      { column: "vat_id_raw", contextKey: "id" },
      { column: "subject_key", contextKey: "subject_key" },
    ],
  },

  // ╭─ Marketplace kinds — Gal building 2 of these now (project_interest + ?) ─╮
  project_interest: {
    slug: "project_interest",
    labelHe: "מתעניין בפרויקט",
    status: "coming-soon",
    origin: "marketplace",
    comingSoonReason: "בבנייה כעת — צפוי בקרוב",
  },
  company_sale: {
    slug: "company_sale",
    labelHe: "מוכר חברה",
    status: "coming-soon",
    origin: "marketplace",
    comingSoonReason: "בתכנון",
  },
  company_buyer: {
    slug: "company_buyer",
    labelHe: "מתעניין ברכישת חברה",
    status: "coming-soon",
    origin: "marketplace",
    comingSoonReason: "בתכנון",
  },

  // vTag — the universal tagging vocabulary as a browsable lens (the ninth
  // marketplace lens; DEC-0035 three-level taxonomy). Read-only browsing:
  // curation happens in the vtag system field (VTagField), bindings in vtag_bindings.
  vtag: {
    slug: "vtag",
    labelHe: "תג",
    status: "ready",
    origin: "marketplace",
    table: "vtags",
    idColumn: "id",
    displayField: "name",
    subtitleField: "description_he",
    fieldMappings: [
      { column: "source_key", contextKey: "key" },
      { column: "vocabulary_slug", contextKey: "vocabulary" },
      { column: "color", contextKey: "color" },
    ],
  },

  // ╭─ CRM lenses — vb_records lens pattern (entity_type column) ─╮
  // All 12 share the same vb_records substrate; subtle differences in
  // displayField / subtitleField match what existing rows actually use.
  contractor: {
    slug: "contractor",
    labelHe: "קבלן רשום",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "contractor",
    fieldMappings: [
      ...VB_RECORDS_BASE_MAPPINGS,
      { metaKey: "registration_status", contextKey: "registration_status" },
      { metaKey: "business", contextKey: "business" },
    ],
  },
  contact: {
    slug: "contact",
    labelHe: "איש קשר",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "contact",
    fieldMappings: VB_RECORDS_BASE_MAPPINGS,
  },
  client: {
    slug: "client",
    labelHe: "לקוח",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "client",
    fieldMappings: [
      ...VB_RECORDS_BASE_MAPPINGS,
      { metaKey: "business", contextKey: "business" },
      { metaKey: "service_area", contextKey: "service_area" },
    ],
  },
  deal: {
    slug: "deal",
    labelHe: "עסקה",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "deal",
    fieldMappings: [
      ...VB_RECORDS_BASE_MAPPINGS,
      { metaKey: "pipeline_stage", contextKey: "pipeline_stage" },
      { metaKey: "deal_value", contextKey: "deal_value" },
    ],
  },
  lead: {
    slug: "lead",
    labelHe: "ליד",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "lead",
    fieldMappings: [
      ...VB_RECORDS_BASE_MAPPINGS,
      { metaKey: "pipeline_stage", contextKey: "pipeline_stage" },
      { metaKey: "qualification_score", contextKey: "qualification_score" },
    ],
  },
  agreement: {
    slug: "agreement",
    labelHe: "הסכם",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "agreement",
    fieldMappings: VB_RECORDS_BASE_MAPPINGS,
  },
  employee: {
    slug: "employee",
    labelHe: "עובד",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "employee",
    fieldMappings: [
      ...VB_RECORDS_BASE_MAPPINGS,
      { metaKey: "skills", contextKey: "skills" },
      { metaKey: "capacity", contextKey: "capacity" },
      { metaKey: "hourly_rate", contextKey: "hourly_rate" },
      { metaKey: "preferred_areas", contextKey: "preferred_areas" },
    ],
  },
  vendor: {
    slug: "vendor",
    labelHe: "ספק / קבלן",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "vendor",
    fieldMappings: [
      ...VB_RECORDS_BASE_MAPPINGS,
      { metaKey: "business", contextKey: "business" },
      { metaKey: "service_area", contextKey: "service_area" },
    ],
  },
  property: {
    slug: "property",
    labelHe: "נכס / אתר בנייה",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "property",
    fieldMappings: [
      ...VB_RECORDS_BASE_MAPPINGS,
      { metaKey: "address", contextKey: "address" },
      { metaKey: "construction_stage", contextKey: "construction_stage" },
    ],
  },
  invoice: {
    slug: "invoice",
    labelHe: "חשבונית",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "invoice",
    fieldMappings: [
      ...VB_RECORDS_BASE_MAPPINGS,
      { metaKey: "currency", contextKey: "currency" },
      { metaKey: "payment_status", contextKey: "payment_status" },
    ],
  },
  case: {
    slug: "case",
    labelHe: "תיק שירות",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "case",
    fieldMappings: [
      ...VB_RECORDS_BASE_MAPPINGS,
      { metaKey: "case_status", contextKey: "case_status" },
      { metaKey: "service_type", contextKey: "service_type" },
    ],
  },

  // ╭─ Task lenses ─╮
  task: {
    slug: "task",
    labelHe: "משימה",
    status: "ready",
    origin: "marketplace",
    // ⚠️ הוסב 18.09.2026, הכרעת גל באותו יום. קודם לכן task היה עדשה מעל
    // vb_records עם שלוש רשומות בלבד — שתי דוגמאות ממאי ורשומה אמיתית אחת
    // מ-07.09 — בעוד pm_tasks מחזיקה 1,245 משימות חיות מתוך 1,276.
    // זהו בדיוק הדפוס של project כאן למעלה, שכבר מצביע על pm_projects
    // מאז 29.04: הסוג הוא הזהות, והטבלה הייעודית היא המקור.
    table: "pm_tasks",
    idColumn: "id",
    deletedColumn: "deleted_at",
    displayField: "title",
    subtitleField: "status",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "notion_id", contextKey: "notion_id" },
      { column: "status", contextKey: "status" },
      { column: "priority", contextKey: "priority" },
      { column: "app_id", contextKey: "app_id" },
      { column: "project_id", contextKey: "project_id" },
    ],
  },
  document: {
    slug: "document",
    labelHe: "מסמך",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "document",
    fieldMappings: VB_RECORDS_BASE_MAPPINGS,
  },
  os_node: {
    slug: "os_node",
    labelHe: "צומת במפת OS",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "os_node",
    fieldMappings: VB_RECORDS_BASE_MAPPINGS,
  },
  dev_task: {
    slug: "dev_task",
    labelHe: "משימת פיתוח",
    status: "ready",
    origin: "tiptap-notes",
    table: "vb_records",
    idColumn: "id",
    displayField: "title",
    subtitleField: "description",
    entityTypeFilter: "dev_task",
    fieldMappings: VB_RECORDS_BASE_MAPPINGS,
  },

  // ╭─ Purpose-built tables — data that ran ahead of the entity layer ─╮
  // ⚠️ נוסף 09.08.2026. שמונה טבלאות שכבר החזיקו נתונים חיים ומעולם לא
  // נרשמו כישויות — כלומר מסך הישויות לא ידע עליהן. נמדד באותו יום:
  // closure_reason_taxonomy 66 · comm_messages 145 · pm_team 11 ·
  // knowledge_streams 9 · skills 9 · knowledge_departments 8 ·
  // calendar_events 5 · call_logs 0.
  //
  // הן **לא** הועברו ל-vb_records. העברה הייתה נוגעת ב-253 שורות חיות
  // ובכל מי שקורא להן; עדשה מעל הטבלה הקיימת נותנת את אותו מסך בעלות אפס
  // ומתבטלת בצעד אחד. זהו בדיוק הדפוס של talent/job שמעליהן.
  //
  // מקור: מיפוי 65 ישויות היעד — ראה ספר `REF-entity-target-map`.
  skill: {
    slug: "skill",
    labelHe: "כישור",
    status: "ready",
    origin: "marketplace",
    table: "skills",
    idColumn: "id",
    displayField: "name",
    subtitleField: "category",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "slug", contextKey: "slug" },
      { column: "category", contextKey: "category" },
    ],
  },
  team: {
    slug: "team",
    labelHe: "צוות",
    status: "ready",
    origin: "marketplace",
    table: "pm_team",
    idColumn: "id",
    deletedColumn: "deleted_at",
    displayField: "name",
    subtitleField: "role",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "email", contextKey: "email" },
      { column: "role", contextKey: "role" },
    ],
  },
  department: {
    slug: "department",
    labelHe: "מחלקה",
    status: "ready",
    origin: "marketplace",
    table: "knowledge_departments",
    idColumn: "id",
    displayField: "name_he",
    subtitleField: "description",
    fieldMappings: [{ column: "id", contextKey: "entity_id" }],
  },
  stream: {
    slug: "stream",
    labelHe: "ערוץ תקשורת",
    status: "ready",
    origin: "marketplace",
    table: "knowledge_streams",
    idColumn: "id",
    displayField: "name_he",
    subtitleField: "description",
    fieldMappings: [{ column: "id", contextKey: "entity_id" }],
  },
  message: {
    slug: "message",
    labelHe: "הודעה",
    status: "ready",
    origin: "marketplace",
    table: "comm_messages",
    idColumn: "id",
    displayField: "body",
    subtitleField: "sender_name",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "entity_phone", contextKey: "phone" },
      { column: "channel", contextKey: "channel" },
    ],
  },
  calendar_event: {
    slug: "calendar_event",
    labelHe: "אירוע לוח שנה",
    status: "ready",
    origin: "marketplace",
    table: "calendar_events",
    idColumn: "id",
    displayField: "title",
    subtitleField: "organizer_email",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "organizer_email", contextKey: "email" },
    ],
  },
  call: {
    slug: "call",
    labelHe: "שיחה",
    status: "ready",
    origin: "marketplace",
    table: "call_logs",
    idColumn: "id",
    displayField: "description",
    subtitleField: "phone",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "phone", contextKey: "phone" },
    ],
  },
  closure_reason: {
    slug: "closure_reason",
    labelHe: "סיבת סגירה",
    status: "ready",
    origin: "marketplace",
    table: "closure_reason_taxonomy",
    idColumn: "id",
    displayField: "reason_text",
    subtitleField: "classification",
    fieldMappings: [{ column: "id", contextKey: "entity_id" }],
  },

  // ╭─ The pilot: an entity of entities ─╮
  //
  // DEC-0409 step 5 (docs/specs/universal-instance-contract.md): the first
  // pilot is `entity_type` itself — the row that defines a table, opened as
  // an instance like any other row. Gal locked it on 09.09.2026 because it
  // tests the contract on an object the system already knows, with no new
  // data imported and no invented business domain.
  //
  // Measured 18.09.2026: entity_types held 75 rows, none of them a type of
  // types. Gal approved the data row the same night; it was applied as
  // migration 20260917235244_dec0409_step5_entity_type_pilot.sql, and the
  // table now holds 76 rows, the pilot among them.
  //
  // ⚠️ label is jsonb ({he,en,ru}), not a text column — the same trap as
  // pm_apps.name. displayJsonbKey names the path, and the generic adapter
  // reads it; displayField stays as the fallback for a row with no label.
  entity_type: {
    slug: "entity_type",
    labelHe: "סוג ישות",
    status: "ready",
    origin: "marketplace",
    table: "entity_types",
    idColumn: "id",
    displayField: "slug",
    displayJsonbKey: { column: "label", key: "he" },
    subtitleField: "slug",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "slug", contextKey: "slug" },
      { column: "registry_kind", contextKey: "registry_kind" },
      { column: "domain", contextKey: "domain" },
      { column: "parent_id", contextKey: "parent_id" },
      { column: "instance_record_id", contextKey: "instance_record_id" },
    ],
  },

  // ╭─ Fireberry mirror lenses — fb_records by object_type_id (read-only) ─╮
  // The real business book (285K rows). Same lens-on-record engine, with
  // `object_type_id` as the filter column and the `data` jsonb as meta.
  // Writes are forbidden — fb_records is a mirror, not a canonical target.
  fireberry_contact: {
    slug: "fireberry_contact",
    labelHe: "איש קשר",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    filterColumn: "object_type_id",
    entityTypeFilter: "2", // Fireberry object 2 = איש קשר
    metaColumn: "data",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { metaKey: "telephone1", contextKey: "phone" },
      { metaKey: "emailaddress1", contextKey: "email" },
      { column: "name_value", contextKey: "name" },
    ],
  },
  fireberry_account: {
    slug: "fireberry_account",
    labelHe: "בית עסק",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    filterColumn: "object_type_id",
    entityTypeFilter: "1", // Fireberry object 1 = לקוח / בית עסק
    metaColumn: "data",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { metaKey: "telephone1", contextKey: "phone" },
      { metaKey: "emailaddress1", contextKey: "email" },
      { column: "name_value", contextKey: "name" },
    ],
  },

  // ╭─ משפחות 07 ו-08 — הצדדים והחיבור, כולם מעל המראה ─╮
  // ⚠️ נוסף 09.08.2026. שתי המשפחות האלה **נראו ריקות לגמרי** בבדיקה
  // הראשונה, כי חיפשתי טבלאות ייעודיות ולא מצאתי. הן לא היו ריקות —
  // הן יושבות ב-`fb_records` ומעולם לא נרשמו. נמדד באותו יום:
  //
  //   08.2 מתעניינים בחברות   928  ·  08.5 קישור מתעניין↔חברה  777
  //   08.1 חברה למכירה        132  ·  07.2 מתעניינים בפרויקטים  84
  //   07.1 פרויקטים            30  ·  07.5 חיבור קבלן↔פרויקט     19
  //
  // סה"כ 1,970 שורות חיות, **ואפס טבלאות חדשות נדרשו**. גל: *"הכל אמור
  // להיות מסונכרן מ-Fireberry ל-Supabase"* — וזה בדיוק מה שהמראה עושה.
  //
  // ⚠️ **דפוס ה-X.5:** בכל משפחה עסקית ב-Fireberry האובייקט `X.5` הוא
  // **אובייקט הקישור** בין שני הצדדים. `02.5` (עובדים↔לקוח) הוא
  // `matching_records` עם 953 שורות; כאן נרשמים שני האחים שלו.
  fb_project: {
    slug: "fb_project",
    labelHe: "פרויקט (Fireberry)",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    filterColumn: "object_type_id",
    entityTypeFilter: "46", // 07.1 פרויקטים
    metaColumn: "data",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { column: "name_value", contextKey: "name" },
    ],
  },
  fb_project_interest: {
    slug: "fb_project_interest",
    labelHe: "מתעניין בפרויקט",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    filterColumn: "object_type_id",
    entityTypeFilter: "1016", // 07.2
    metaColumn: "data",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { metaKey: "telephone1", contextKey: "phone" },
      { column: "name_value", contextKey: "name" },
    ],
  },
  fb_project_link: {
    slug: "fb_project_link",
    labelHe: "חיבור קבלן ↔ פרויקט",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    filterColumn: "object_type_id",
    entityTypeFilter: "1065", // 07.5 — אובייקט הקישור של משפחה 07
    metaColumn: "data",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { column: "name_value", contextKey: "name" },
    ],
  },
  fb_company_sale: {
    slug: "fb_company_sale",
    labelHe: "חברה למכירה",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    filterColumn: "object_type_id",
    entityTypeFilter: "1000", // 08.1
    metaColumn: "data",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { column: "name_value", contextKey: "name" },
    ],
  },
  fb_company_buyer: {
    slug: "fb_company_buyer",
    labelHe: "מתעניין בחברה",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    filterColumn: "object_type_id",
    entityTypeFilter: "1002", // 08.2 — התחנה העמוסה ביותר במשפחה
    metaColumn: "data",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { metaKey: "telephone1", contextKey: "phone" },
      { metaKey: "emailaddress1", contextKey: "email" },
      { column: "name_value", contextKey: "name" },
    ],
  },
  fb_company_link: {
    slug: "fb_company_link",
    labelHe: "קישור מתעניין ↔ חברה",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    filterColumn: "object_type_id",
    entityTypeFilter: "1051", // 08.5 — אובייקט הקישור של משפחה 08
    metaColumn: "data",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { column: "name_value", contextKey: "name" },
    ],
  },

  // ╭─ Matching-source lenses — the text the scoring engine reads ─╮
  // גל בחר את השלושה האלה מתוך 14 אובייקטים גדולים, בקריטריון אחד:
  // הם טקסט על המועמד, ולא לוג. WhatsApp (74,969) ופניות (33,944) גדולים
  // בהרבה ונשארו בחוץ בכוונה — לוג רשום כישות מחזיר את הבלגן.
  fb_employment_history: {
    slug: "fb_employment_history",
    labelHe: "היסטוריה תעסוקתית",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    subtitleField: "pcfjobtitle",
    filterColumn: "object_type_id",
    entityTypeFilter: "1061", // 02.1.1 — 12,136 שורות
    metaColumn: "data",
    // ⚠️ `pcfjobdescription` מלא ב-10,465 מתוך 12,136 שורות, ממוצע 154 תווים,
    // על פני 2,353 אנשים. זה החומר שרכיב semantic_similarity אמור לקרוא,
    // והוא אינו קיים בטבלת `talents` — ולכן העדשה הזאת קודמת למילוי
    // ה-embedding ולא להפך.
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { column: "name_value", contextKey: "name" },
      { metaKey: "pcfcontactname", contextKey: "person" },
      // ⚠️ שמות השדות ב-Fireberry הפוכים למשמעותם, ונבדק על מדגם:
      // `name_value` מחזיק את **התפקיד** ("מנהל פרויקט", "בעלים ומייסד"),
      // ו-`pcfjobtitle` מחזיק את **המעסיק** ("רכבת ישראל", "ישראל פקטורי").
      // מיפוי לפי שם השדה היה מציג את שם החברה בעמודת התפקיד.
      { column: "name_value", contextKey: "role" },
      { metaKey: "pcfjobtitle", contextKey: "employer" },
      { metaKey: "pcfjobdescription", contextKey: "description" },
      { metaKey: "pcfenddate", contextKey: "ended_at" },
    ],
  },
  fb_talent: {
    slug: "fb_talent",
    labelHe: "בעל כישורים (Fireberry)",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    subtitleField: "pcfpoststatustext",
    filterColumn: "object_type_id",
    entityTypeFilter: "1001", // 02.1 — 3,876 שורות
    metaColumn: "data",
    // ⚠️ אינה כפילות של `talent`. `talent` יושבת מעל טבלת `talents`
    // הייעודית (3,874 שורות), וזו המראה של המקור שהזין אותה — בדיוק
    // היחס שכבר קיים בין `contact` לבין `fireberry_contact`.
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { column: "name_value", contextKey: "name" },
      { metaKey: "pcfcontactid", contextKey: "contact_id" },
      { metaKey: "pcfpoststatusname", contextKey: "status" },
      { metaKey: "pcfallareasname", contextKey: "areas" },
      { metaKey: "pcforiginname", contextKey: "origin" },
    ],
  },
  fb_incoming_cv: {
    slug: "fb_incoming_cv",
    labelHe: "קורות חיים נכנסים",
    status: "ready",
    origin: "fireberry-mirror",
    table: "fb_records",
    idColumn: "id",
    displayField: "name_value",
    subtitleField: "pcfcityname",
    filterColumn: "object_type_id",
    entityTypeFilter: "1047", // 02.6 — 3,793 שורות, כולל פענוח AI
    metaColumn: "data",
    fieldMappings: [
      { column: "id", contextKey: "entity_id" },
      { column: "fireberry_id", contextKey: "source_id" },
      { column: "name_value", contextKey: "name" },
      { metaKey: "pcftelephone", contextKey: "phone" },
      { metaKey: "pcfemail", contextKey: "email" },
      { metaKey: "pcfcityname", contextKey: "city" },
      { metaKey: "pcfcontactname", contextKey: "person" },
    ],
  },

  // ╭─ Reference vocabularies — shared "databases" (reference_items) ─╮
  ...Object.fromEntries(
    REFERENCE_LISTS.map((r) => [r.slug, referenceSource(r.slug, r.labelHe)]),
  ),
};

/** Resolve a slug to its source. Returns null for unknown slugs (caller 404s). */
export function resolveEntitySource(slug: string): EntitySource | null {
  return SOURCES[slug] ?? null;
}

/** Type guard — does this slug exist in the registry? */
export function isKnownSlug(slug: string): boolean {
  return slug in SOURCES;
}

/** Type guard — is this slug ready (has real backend wired)? */
export function isReadySlug(slug: string): boolean {
  const src = SOURCES[slug];
  return src?.status === "ready";
}

/** All sources, for enumeration UIs (admin panels, link generators). */
export function listAllSources(): EntitySource[] {
  return Object.values(SOURCES);
}

/** Only ready sources — useful for "links you can actually open" lists. */
export function listReadySources(): EntitySource[] {
  return Object.values(SOURCES).filter((s) => s.status === "ready");
}

/** Only the reference-vocabulary sources (expense_category, payment_instrument,
 *  ownership_type, currency). For select-field option resolution + admin UIs. */
export function listReferenceSources(): EntitySource[] {
  return Object.values(SOURCES).filter((s) => s.origin === "reference");
}

/** Is this slug a shared reference vocabulary (vs a primary CRM/marketplace entity)? */
export function isReferenceSlug(slug: string): boolean {
  return SOURCES[slug]?.origin === "reference";
}
